import React from "react";
import { useTranslation } from "react-i18next";
import { Doughnut } from "react-chartjs-2";
import { Chart as ChartJS, ArcElement, Tooltip } from "chart.js";

ChartJS.register(ArcElement, Tooltip);

// chart.js draws on canvas and cannot resolve var(--x), so read the token once per render
const token = (name, fallback) =>
  (typeof window !== "undefined" &&
    getComputedStyle(document.documentElement).getPropertyValue(name).trim()) ||
  fallback;

export default function GaugeWidget({
  title,
  value,
  unit,
  min = 0,
  max = 100,
  auto = false,
  outOfRange = null,
  thresholds = { warning: 70, danger: 90 },
}) {
  const { t } = useTranslation();
  const span = max - min;
  const percentage = span > 0
    ? Math.min(100, Math.max(0, ((value - min) / span) * 100))
    : 0;

  let color = token("--vx-ok", "#1b7f4b");
  if (outOfRange || percentage >= thresholds.danger) {
    color = token("--vx-danger", "#c53030");
  } else if (percentage >= thresholds.warning) {
    color = token("--vx-warn", "#b86e00");
  }

  const data = {
    datasets: [
      {
        data: [percentage, 100 - percentage],
        backgroundColor: [color, token("--vx-surface-sunken", "#f1f3f7")],
        borderWidth: 0,
        circumference: 270,
        rotation: 225,
      },
    ],
  };

  const options = {
    responsive: true,
    maintainAspectRatio: false,
    cutout: "75%",
    plugins: {
      tooltip: { enabled: false },
    },
  };

  const warning =
    outOfRange === "above"
      ? t("home.grid.gaugeAbove", { max })
      : outOfRange === "below"
      ? t("home.grid.gaugeBelow", { min })
      : null;

  return (
    <div style={styles.container}>
      <div style={styles.title}>{title}</div>
      <div style={styles.chartArea}>
        <div style={styles.chartWrapper}>
          <Doughnut data={data} options={options} />
          <div style={styles.valueOverlay}>
            <span style={{ ...styles.value, color }}>{value}</span>
            <span style={styles.unit}>{unit}</span>
          </div>
        </div>
        <div style={styles.range}>
          <span>{min}</span>
          <span>{max}</span>
        </div>
        {(warning || auto) && (
          <div
            style={{ ...styles.note, color: warning ? "var(--vx-danger)" : "var(--vx-text-2)" }}
            role={warning ? "alert" : undefined}
          >
            {warning || t("home.grid.gaugeAuto")}
          </div>
        )}
      </div>
    </div>
  );
}

const styles = {
  container: {
    backgroundColor: "var(--vx-surface)",
    fontFamily: "var(--vx-font-sans)",
    padding: "8px",
    height: "100%",
    width: "100%",
    display: "flex",
    flexDirection: "column",
    alignItems: "center",
    boxSizing: "border-box",
    overflow: "hidden",
  },
  title: {
    color: "var(--vx-text-2)",
    fontSize: "12px",
    fontWeight: 600,
    flexShrink: 0,
    maxWidth: "100%",
    overflow: "hidden",
    textOverflow: "ellipsis",
    whiteSpace: "nowrap",
  },
  chartArea: {
    flex: 1,
    width: "100%",
    minHeight: 0,
    display: "flex",
    flexDirection: "column",
    alignItems: "center",
    justifyContent: "center",
    position: "relative",
  },
  chartWrapper: {
    position: "relative",
    width: "100%",
    height: "100%",
    maxWidth: "100%",
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
  },
  valueOverlay: {
    position: "absolute",
    top: "55%",
    left: "50%",
    transform: "translate(-50%, -50%)",
    textAlign: "center",
  },
  value: {
    fontFamily: "var(--vx-font-mono)",
    fontSize: "clamp(14px, 1.6vw, 22px)",
    fontWeight: 500,
    display: "block",
  },
  unit: {
    fontSize: "clamp(9px, 1vw, 12px)",
    color: "var(--vx-text-2)",
  },
  range: {
    display: "flex",
    justifyContent: "space-between",
    color: "var(--vx-text-2)",
    fontFamily: "var(--vx-font-mono)",
    fontSize: "10px",
    width: "80%",
    flexShrink: 0,
  },
  note: {
    fontSize: "11px",
    flexShrink: 0,
    paddingTop: "2px",
  },
};
